import {
  APP_INITIALIZER,
  ErrorHandler,
  makeEnvironmentProviders,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Integration } from '@sentry/core';

export interface SentryInitConfig {
  allowUrls?: (string | RegExp)[];
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate?: number;
  debug?: boolean;
  enabled?: boolean;
  sendDefaultPii?: boolean;
  dist?: string;
  tracePropagationTargets?: (string | RegExp)[];
  integrations?: {
    /** Registered synchronously during `Sentry.init()`. Defaults to on. */
    browserTracing?: boolean;
    /**
     * Opt-in. Loaded from Sentry's CDN after bootstrap, never in the initial bundle. `true` loads
     * it once the browser is idle; `'on-demand'` waits for the app to call {@link loadSentryReplay},
     * for an app that times its own third-party loads.
     */
    replay?: boolean | 'on-demand';
    /** Opt-in. Loaded from a lazy chunk after bootstrap, never in the initial bundle. */
    feedback?: boolean;
  };
  applicationKey?: string;
}

let replayOnDemand = false;
let replayRequested = false;

export function initializeSentry(config: SentryInitConfig): void {
  replayOnDemand =
    config.enabled !== false && config.integrations?.replay === 'on-demand';
  replayRequested = false;

  if (config.enabled === false) {
    console.warn('Sentry is disabled');
    return;
  }

  const integrations: Integration[] = [];

  if (config.integrations?.browserTracing !== false) {
    integrations.push(Sentry.browserTracingIntegration());
  }

  if (config.applicationKey) {
    integrations.push(
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [config.applicationKey],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    );
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: config.tracesSampleRate ?? 0.1,
    debug: config.debug ?? false,
    sendDefaultPii: config.sendDefaultPii ?? true,
    tracePropagationTargets: config.tracePropagationTargets ?? ['localhost'],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: config.tracesSampleRate ?? 0.1,
    dist: config.dist,
    integrations,
    allowUrls: config.allowUrls,
  });

  scheduleDeferredIntegrations(config);
}

/**
 * Registers the heavyweight integrations after bootstrap so their code stays out of the initial
 * bundle.
 *
 * These are fetched from Sentry's CDN rather than bundled. A local dynamic `import()` cannot move
 * them: `@sentry/browser`'s index module is in the initial chunk (the `Sentry.init()` above needs
 * it) and statically imports both payloads, so esbuild keeps them there no matter which chunk
 * references them. Measured on this repo: deferring locally left the initial bundle 8.7 kB *larger*.
 *
 * The CDN URL is derived from the installed `SDK_VERSION`, so a Sentry upgrade repoints it. That is
 * verified in CI by `tools/verify-sentry-cdn/verify-sentry-cdn.mjs`, which fails the build if the
 * bundles for the installed version are not published — the gap that existed for the whole
 * 8.0.0-8.26.0 range.
 */
function scheduleDeferredIntegrations(config: SentryInitConfig): void {
  if (config.integrations?.replay === true) {
    whenIdle(() => loadIntegration('replayIntegration'));
  }

  if (config.integrations?.feedback === true) {
    whenIdle(() => loadIntegration('feedbackIntegration'));
  }
}

/**
 * Registers replay for an app initialized with `replay: 'on-demand'`. It runs once, and it is a
 * no-op under any other configuration (Sentry disabled included), so the caller needs to know
 * nothing about how Sentry was set up.
 */
export function loadSentryReplay(): void {
  if (!replayOnDemand || replayRequested) {
    return;
  }

  replayRequested = true;
  loadIntegration('replayIntegration');
}

/**
 * Fire-and-forget: a bundle that fails to load must never break bootstrap or surface as an
 * unhandled rejection. Error tracking itself keeps working without it.
 */
function loadIntegration(
  name: 'replayIntegration' | 'feedbackIntegration',
): void {
  Sentry.lazyLoadIntegration(name)
    .then((integration) => Sentry.addIntegration(integration()))
    .catch(() => undefined);
}

/**
 * `requestIdleCallback` has no deadline of its own: a busy main thread can defer the callback
 * indefinitely, and Chrome does not run idle callbacks at all in a tab that never gains
 * visibility. Without a ceiling a session opened in a background tab would never start
 * recording — the more capable branch would be the one that never fires.
 */
const IDLE_DEADLINE_MS = 5_000;

function whenIdle(callback: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => callback(), { timeout: IDLE_DEADLINE_MS });
    return;
  }

  setTimeout(callback, 0);
}

export function provideSentry() {
  return makeEnvironmentProviders([
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ]);
}
